import { useContext, useMemo } from 'react';

import ArtworkDataContext from '@/context/ArtworkDataContext';
import useStateHandler from '@/hooks/useStateHandler';
import ArtworkAuctionDesktop from '@/pages/artwork/content-section/components/ArtworkAuctionDesktop';
import useArtworkAuctionController from '@/pages/artwork/hooks/useArtworkAuctionController';

import './ArtworkAuctionMobile.css';

export default function ArtworkAuctionMobile({ open, onClose, artworkData: artworkDataProp }) {
  const context = useContext(ArtworkDataContext);
  const contextArtworkData = context?.artworkData;
  // Allow external data injection (top-level bottom sheet) while keeping support for in-context usage.
  const artworkData = artworkDataProp || contextArtworkData;
  const { state } = useStateHandler();

  const {
    artworkData: resolvedArtworkData,
    pendingBidState,
    handlePlaceBid,
    isCountdownFrozen,
    serverTimeOffsetMs,
    isAuctionArtwork,
    isAuctionClosed,
    isBiddingBlocked,
    paymentMethods,
    selectedPaymentMethodId,
    setSelectedPaymentMethodId,
    isLoadingPaymentMethods,
    paymentMethodsError,
    hasPaymentMethods,
    refreshPaymentMethods,
    handleManagePaymentMethods,
  } = useArtworkAuctionController({
    artworkData,
    userSession: state?.user_session ?? null,
    isActive: open,
  });

  const showBidPanel = useMemo(
    () => (!isBiddingBlocked || isAuctionClosed),
    [isAuctionClosed, isBiddingBlocked],
  );

  if (!resolvedArtworkData) {
    return (
      <div className="artwork-auction-mobile__empty">
        Loading auction data…
      </div>
    );
  }

  if (!isAuctionArtwork) {
    return (
      <div className="artwork-auction-mobile__empty">
        This artwork is not part of an auction.
      </div>
    );
  }

  // Reuse the desktop auction surface to keep the visual language identical across breakpoints.
  return (
    <div className="artwork-auction-mobile">
      <ArtworkAuctionDesktop
        artworkData={resolvedArtworkData}
        onBack={onClose}
        onPlaceBid={handlePlaceBid}
        isCountdownFrozen={isCountdownFrozen}
        serverTimeOffsetMs={serverTimeOffsetMs}
        pendingBidState={pendingBidState}
        showBidPanel={showBidPanel}
        paymentMethods={paymentMethods}
        selectedPaymentMethodId={selectedPaymentMethodId}
        onSelectPaymentMethod={setSelectedPaymentMethodId}
        onReloadPaymentMethods={refreshPaymentMethods}
        isPaymentMethodLoading={isLoadingPaymentMethods}
        paymentMethodsError={paymentMethodsError}
        hasPaymentMethods={hasPaymentMethods}
        onManagePaymentMethods={handleManagePaymentMethods}
        viewMode="mobile"
      />
    </div>
  );
}
